import { useState, useEffect } from 'react';
import { X, ArrowRight, Navigation, Bell, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../providers/AuthProvider';
import { supabase } from '../lib/supabaseClient';
import { Button } from '@/components/ui/button';

const tourSteps = [
  {
    id: 'navigation',
    title: 'Her er din navigation',
    description: 'Brug navigationen nederst på skærmen til at komme rundt i appen. Her finder du alle vigtige funktioner.',
    target: '[data-tour="bottom-nav"]',
    position: 'top'
  },
  {
    id: 'notifications',
    title: 'Her ser du beskeder',
    description: 'Klik på klokke-ikonet i toppen for at se notifikationer om nye vagter, godkendelser og vigtige opdateringer.',
    target: '[data-tour="notification-bell"]',
    position: 'bottom'
  },
  {
    id: 'profile',
    title: 'Udfyld din profil',
    description: 'Gå til din profil for at opdatere dine oplysninger, kompetencer og dokumenter. En komplet profil hjælper med at matche dig med de rigtige vagter.',
    target: '[data-tour="profile-link"]',
    position: 'top',
    action: '/profile'
  }
];

export default function FTUETour() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    const checkFirstLogin = async () => {
      if (!user?.id) return;

      try {
        // Check if user has completed tour
        const tourCompleted = localStorage.getItem(`ftue_tour_completed_${user.id}`);
        if (tourCompleted) {
          return; // Tour already completed
        }

        // Check if user is first-time (no profile updates or first login)
        const { data: profile } = await supabase
          .from('profiles')
          .select('created_at, updated_at')
          .eq('id', user.id)
          .single();

        if (profile) {
          const created = new Date(profile.created_at);
          const updated = profile.updated_at ? new Date(profile.updated_at) : created;
          const daysSinceCreation = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
          
          // Show tour if account is less than 7 days old and hasn't been updated much
          if (daysSinceCreation < 7 && (updated.getTime() - created.getTime()) < 60000) {
            // Small delay to ensure page is loaded
            setTimeout(() => {
              setIsVisible(true);
              setShowOverlay(true);
            }, 1000);
          }
        }
      } catch (error) {
        console.error('Error checking first login:', error);
      }
    };

    checkFirstLogin();
  }, [user?.id]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = ({ followAction = true } = {}) => {
    if (user?.id) {
      localStorage.setItem(`ftue_tour_completed_${user.id}`, 'true');
    }
    setIsVisible(false);
    setShowOverlay(false);

    // Navigate to action if exists — men bare når brugeren selv afsluttede touren.
    // Auto-afslutning (targets mangler, fx på /reset/confirm) må ikke kapre siden.
    const step = tourSteps[currentStep];
    if (followAction && step?.action) {
      navigate(step.action);
    }
  };

  // Check if target element exists
  useEffect(() => {
    if (!isVisible || currentStep >= tourSteps.length) return;

    const step = tourSteps[currentStep];
    const checkElement = () => {
      const targetElement = document.querySelector(step.target);
      
      if (!targetElement) {
        // Target not found, skip to next step or complete
        if (currentStep < tourSteps.length - 1) {
          setTimeout(() => setCurrentStep(currentStep + 1), 500);
        } else {
          handleComplete({ followAction: false });
        }
        return;
      }
    };

    // Check immediately and also after a short delay (for dynamic content)
    checkElement();
    const timeout = setTimeout(checkElement, 500);
    
    return () => clearTimeout(timeout);
  }, [isVisible, currentStep]);

  if (!isVisible || currentStep >= tourSteps.length) {
    return null;
  }

  const step = tourSteps[currentStep];
  const targetElement = document.querySelector(step.target);

  if (!targetElement) {
    return null; // Will be handled by useEffect
  }

  const rect = targetElement.getBoundingClientRect();
  const isTop = step.position === 'top';
  const top = isTop ? Math.max(20, rect.top - 200) : rect.bottom + 20;
  const left = Math.max(160, Math.min(window.innerWidth - 160, rect.left + rect.width / 2));

  return (
    <>
      {/* Overlay */}
      {showOverlay && (
        <div
          className="fixed inset-0 bg-black/50 z-[9998] transition-opacity"
          onClick={handleSkip}
        />
      )}

      {/* Tour Tooltip */}
      <div
        className="fixed z-[9999] w-80 sm:w-96"
        style={{
          top: `${top}px`,
          left: `${left}px`,
          transform: 'translateX(-50%)',
        }}
      >
        <div className="rounded-2xl border border-app-border bg-app-surface p-6 shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {step.id === 'navigation' && <Navigation className="h-5 w-5 text-app-primary" />}
              {step.id === 'notifications' && <Bell className="h-5 w-5 text-app-primary" />}
              {step.id === 'profile' && <User className="h-5 w-5 text-app-primary" />}
              <h3 className="text-lg font-semibold text-app-text">
                {step.title}
              </h3>
            </div>
            <button
              onClick={handleSkip}
              className="text-app-text-tertiary hover:text-app-text transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Description */}
          <p className="text-sm text-app-text-secondary mb-6 leading-relaxed">
            {step.description}
          </p>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs text-app-text-tertiary mb-2">
              <span>Trin {currentStep + 1} af {tourSteps.length}</span>
              <span>{Math.round(((currentStep + 1) / tourSteps.length) * 100)}%</span>
            </div>
            <div className="h-2 rounded-full bg-app-surface/50 overflow-hidden">
              <div
                className="h-full bg-app-primary transition-all duration-300"
                style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1"
            >
              Spring over
            </Button>
            <Button
              onClick={handleNext}
              className="flex-1 gap-2"
            >
              {currentStep < tourSteps.length - 1 ? 'Næste' : 'Kom i gang'}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Arrow */}
        <div
          className={`absolute ${isTop ? 'bottom-0' : 'top-0'} left-1/2 -translate-x-1/2 ${
            isTop ? 'translate-y-full' : '-translate-y-full'
          }`}
        >
          <div
            className={`w-0 h-0 border-8 ${
              isTop
                ? 'border-t-app-surface border-l-transparent border-r-transparent border-b-transparent'
                : 'border-b-app-surface border-l-transparent border-r-transparent border-t-transparent'
            }`}
          />
        </div>
      </div>
    </>
  );
}

